import { Component, OnDestroy, OnInit } from "@angular/core";
import { Router } from "@angular/router";
import { forkJoin, Subject, timer, EMPTY } from "rxjs";
import { switchMap, tap, takeUntil, filter } from "rxjs/operators";
import { OverviewStateService } from "src/state/overview/service";
import { CommonStateService } from "src/state";
import {
    AllocationTypes,
    Overview,
    SimulationOverview,
    MissingPartDetails,
    BookClosureDetails,
    SimulationMonthlyResponse,
} from "src/state/overview/state-models/overview-data";

export interface PanelStat {
    label: string;
    value: number;
    colorClass: "stat-green" | "stat-orange" | "stat-red" | "stat-blue";
    dotClass: "dot-green" | "dot-orange" | "dot-red" | "dot-blue";
}

export interface DashboardPanel {
    title: string;
    iconPath: string;
    stats: PanelStat[];
}

export interface DonutSegment {
    color: string;
    dasharray: string;
    dashoffset: number;
    label: string;
    value: number;
}

export interface AllocationOverview {
    segments: DonutSegment[];
    total: number;
    centerLabel: string;
}

export interface AllocationUnit {
    scaniaUnitId: number;
    name: string;
    iconPath: string;
    allocated: number;
    produce: number;
    errors: number;
    status: "Allocated" | "Pending";
}

export interface AllocationByUnit {
    title: string;
    rows: AllocationUnit[];
}

@Component({
    selector: "cal-dashboard",
    templateUrl: "./dashboard.component.html",
    styleUrls: ["./dashboard.component.css"],
})
export class DashboardComponent implements OnInit, OnDestroy {
    panels: DashboardPanel[] = [
        {
            title: "Component",
            iconPath: "M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5",
            stats: [
                {
                    label: "Produced",
                    value: 0,
                    colorClass: "stat-orange",
                    dotClass: "dot-orange",
                },
                {
                    label: "Allocated",
                    value: 0,
                    colorClass: "stat-green",
                    dotClass: "dot-green",
                },
                {
                    label: "Flow Errors",
                    value: 0,
                    colorClass: "stat-red",
                    dotClass: "dot-red",
                },
            ],
        },
        {
            title: "Final Assembly",
            iconPath:
                "M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z",
            stats: [
                {
                    label: "Produced",
                    value: 0,
                    colorClass: "stat-orange",
                    dotClass: "dot-orange",
                },
                {
                    label: "Allocated",
                    value: 0,
                    colorClass: "stat-green",
                    dotClass: "dot-green",
                },
                {
                    label: "Flow Errors",
                    value: 0,
                    colorClass: "stat-red",
                    dotClass: "dot-red",
                },
            ],
        },
        {
            title: "Simulation",
            iconPath: "M18 20V10M12 20V4M6 20v-6",
            stats: [
                {
                    label: "Total Simulation",
                    value: 0,
                    colorClass: "stat-green",
                    dotClass: "dot-green",
                },
                {
                    label: "SEU Simulation",
                    value: 0,
                    colorClass: "stat-orange",
                    dotClass: "dot-orange",
                },
                {
                    label: "SLA Simulation",
                    value: 0,
                    colorClass: "stat-blue",
                    dotClass: "dot-blue",
                },
                {
                    label: "Errors",
                    value: 0,
                    colorClass: "stat-red",
                    dotClass: "dot-red",
                },
            ],
        },
    ];

    allocationOverviews: AllocationOverview[] = [];

    allocationByUnits: AllocationByUnit[] = [];

    missingPartMarket1: MissingPartDetails[] | null = null;

    simulationMonthlyData: SimulationMonthlyResponse | null = null;

    bookClosureDetails: BookClosureDetails[] | null = null;

    selectedBookClosureDetail: BookClosureDetails | null = null;

    loadingMain = true;
    loadingSimDetails = true;

    private readonly DONUT_CIRC = 2 * Math.PI * 35;
    private readonly SEG_COLORS = ["#5b9bd5", "#6bba8a", "#e57373"];
    private readonly REFRESH_INTERVAL = 120000; // 2 minutes
    private readonly destroy$ = new Subject<void>();
    private tabVisible = true;

    constructor(
        private stateService: OverviewStateService,
        private commonService: CommonStateService,
        private router: Router,
    ) {}

    ngOnInit(): void {
        this.loadbookClosureDetails();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    private listenToVisibility(): void {
        document.addEventListener("visibilitychange", () => {
            this.tabVisible = !document.hidden;
        });
    }

    private startAutoRefresh(): void {
        timer(this.REFRESH_INTERVAL, this.REFRESH_INTERVAL)
            .pipe(
                takeUntil(this.destroy$),
                filter(() => this.tabVisible),
                switchMap(() => {
                    return forkJoin({
                        components: this.stateService.GetByAllocationType(
                            AllocationTypes.Component,
                        ),
                        finalAssembly: this.stateService.GetByAllocationType(
                            AllocationTypes.FinalAssembly,
                        ),
                        simulation: this.stateService.GetSimulation(),
                    });
                }),
            )
            .subscribe(({ components, finalAssembly, simulation }) => {
                this.updatePanelStats(0, components);
                this.updatePanelStats(1, finalAssembly);
                this.updateSimulationPanel(simulation);

                this.allocationOverviews = [
                    this.buildOverview(this.panels[0], components),
                    this.buildOverview(this.panels[1], finalAssembly),
                    this.buildSimulationOverview(simulation),
                ];

                this.allocationByUnits = [
                    {
                        title: "Component Allocation",
                        rows: this.mapOverviewToRows(components),
                    },
                    {
                        title: "Final Assembly Allocation",
                        rows: this.mapOverviewToRows(finalAssembly),
                    },
                ];
            });
    }

    private loadDashboardData(): void {
        const today = new Date();
        const oneMonthAgo = new Date();
        oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);

        const fromDate = oneMonthAgo.toISOString().split("T")[0];
        const productionDate = this.selectedBookClosureDetail
            ? this.toLocalDateString(
                  new Date(this.selectedBookClosureDetail.productionDate),
              )
            : undefined;

        forkJoin({
            components: this.stateService.GetByAllocationType(
                AllocationTypes.Component,
                productionDate,
            ),
            finalAssembly: this.stateService.GetByAllocationType(
                AllocationTypes.FinalAssembly,
                productionDate,
            ),
            simulation: this.stateService.GetSimulation(),
            missingPartMarket:
                this.stateService.GetSimulationMissingPart(fromDate),
            simulationMonthly: this.stateService.GetSimulationMonthlyData(),
        }).subscribe(
            ({ components, finalAssembly, simulation, missingPartMarket, simulationMonthly }) => {
                this.missingPartMarket1 = missingPartMarket;
                this.simulationMonthlyData = simulationMonthly;
                this.loadingSimDetails = false;
                this.updatePanelStats(0, components);
                this.updatePanelStats(1, finalAssembly);
                this.updateSimulationPanel(simulation);

                this.allocationOverviews = [
                    this.buildOverview(this.panels[0], components),
                    this.buildOverview(this.panels[1], finalAssembly),
                    this.buildSimulationOverview(simulation),
                ];

                this.allocationByUnits = [
                    {
                        title: "Component Allocation",
                        rows: this.mapOverviewToRows(components),
                    },
                    {
                        title: "Final Assembly Allocation",
                        rows: this.mapOverviewToRows(finalAssembly),
                    },
                ];

                this.loadingMain = false;
            },
        );
    }

    private refreshSimDetails(): void {
        const today = new Date();
        const oneMonthAgo = new Date();
        oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);

        const fromDate = oneMonthAgo.toISOString().split("T")[0];
        const toDate = today.toISOString().split("T")[0];
    }

    private updateSimulationPanel(data: SimulationOverview): void {
        if (data) {
            this.panels[2].stats[0].value = data.totalSimulation;
            this.panels[2].stats[1].value = data.seuSimulation;
            this.panels[2].stats[2].value = data.slaSimulation;
            this.panels[2].stats[3].value = data.totalSimulation - data.seuSimulation - data.slaSimulation;
        }
    }

    private updatePanelStats(panelIndex: number, data: Overview[]): void {
        const allocated = data.reduce(
            (sum, item) => sum + item.allocatedCount,
            0,
        );
        const produce = data.reduce((sum, item) => sum + item.produceCount, 0);
        const errors = data.reduce(
            (sum, item) => sum + item.produceCountError,
            0,
        );

        this.panels[panelIndex].stats[0].value = produce;
        this.panels[panelIndex].stats[1].value = allocated;
        this.panels[panelIndex].stats[2].value = errors;
    }

    private mapOverviewToRows(data: Overview[]): AllocationUnit[] {
        const defaultIcon =
            "M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5";
        return data.map((item) => ({
            scaniaUnitId: item.scaniaUnitId,
            name: item.name,
            iconPath: defaultIcon,
            allocated: item.allocatedCount,
            produce: item.produceCount,
            errors: item.produceCountError,
            status: this.calcStatus(item),
        }));
    }

    navigateToAllocation(section: AllocationByUnit, row: AllocationUnit): void {
        const route =
            section.title === "Component Allocation"
                ? "/allocate/component"
                : "/allocate/finalassembly";
        const queryParams: any = { scaniaUnitId: row.scaniaUnitId };
        if (this.selectedBookClosureDetail) {
            queryParams.productionDate = this.toLocalDateString(
                new Date(this.selectedBookClosureDetail.productionDate),
            );
        }
        const urlTree = this.router.createUrlTree([route], { queryParams });
        window.open(this.router.serializeUrl(urlTree), "_blank");
    }

    private calcStatus(item: Overview): "Allocated" | "Pending" {
        if (item.allocatedCount === item.totalCount) {
            return "Allocated";
        }
        if (item.produceCount === item.totalCount) {
            return "Pending";
        }
        if (
            item.allocatedCount + item.produceCount + item.produceCountError ===
            item.totalCount
        ) {
            return "Pending";
        }
        return "Pending";
    }

    private buildOverview(
        panel: DashboardPanel,
        data: Overview[],
    ): AllocationOverview {
        const circ = this.DONUT_CIRC;
        const actualTotal = data.reduce((s, item) => s + item.totalCount, 0);
        const statsSum = panel.stats.reduce((s, st) => s + st.value, 0);

        // show a neutral ring when there is no data to avoid color bleed
        if (statsSum === 0) {
            return {
                segments: [
                    {
                        color: "#e0e0e0",
                        dasharray: `${circ} 0`,
                        dashoffset: 0,
                        label: "",
                        value: 0,
                    },
                ],
                total: actualTotal,
                centerLabel: panel.title,
            };
        }

        // only segments with actual value — zero-value arcs cause blink at boundaries
        const active = panel.stats
            .map((stat, i) => ({ stat, color: this.SEG_COLORS[i] }))
            .filter(({ stat }) => stat.value > 0);

        const minArc = circ * 0.02;
        const rawLengths = active.map(
            ({ stat }) => (stat.value / statsSum) * circ,
        );
        const lengths = rawLengths.map((len) => Math.max(len, minArc));
        const scale = circ / lengths.reduce((s, l) => s + l, 0);
        const adjusted = lengths.map((l) => l * scale);

        let cum = 0;
        const segments: DonutSegment[] = active.map(({ stat, color }, i) => {
            // force last segment to close ring exactly — prevents float-drift gap
            const len = i === active.length - 1 ? circ - cum : adjusted[i];
            const seg: DonutSegment = {
                color,
                dasharray: `${len.toFixed(4)} ${(circ - len).toFixed(4)}`,
                dashoffset: parseFloat((-cum).toFixed(4)),
                label: stat.label,
                value: stat.value,
            };
            cum += len;
            return seg;
        });

        return { segments, total: actualTotal, centerLabel: panel.title };
    }

    private buildSimulationOverview(
        data: SimulationOverview,
    ): AllocationOverview {
        const circ = this.DONUT_CIRC;
        const seuColor = "#5b9bd5";
        const slaColor = "#6bba8a";
        const errColor = "#e57373";

        const errorsValue = (data?.totalSimulation || 0) - (data?.seuSimulation || 0) - (data?.slaSimulation || 0);
        const simData = [
            { value: data?.seuSimulation || 0, color: seuColor, label: "SEU Simulation" },
            { value: data?.slaSimulation || 0, color: slaColor, label: "SLA Simulation" },
            { value: Math.max(0, errorsValue),  color: errColor, label: "Errors" },
        ];
        const total = data?.totalSimulation || 0;

        // show neutral ring when there is no simulation data
        if (total === 0) {
            return {
                segments: [{ color: "#e0e0e0", dasharray: `${circ} 0`, dashoffset: 0, label: "", value: 0 }],
                total: 0,
                centerLabel: "Simulation",
            };
        }

        const active = simData.filter((item) => item.value > 0);
        const activeSum = active.reduce((s, item) => s + item.value, 0);
        const minArc = circ * 0.02;
        const lengths = active.map((item) =>
            Math.max((item.value / activeSum) * circ, minArc),
        );
        const scale = circ / lengths.reduce((s, l) => s + l, 0);
        const adjusted = lengths.map((l) => l * scale);

        let cum = 0;
        const segments: DonutSegment[] = active.map((item, i) => {
            const len = i === active.length - 1 ? circ - cum : adjusted[i];
            const seg: DonutSegment = {
                color: item.color,
                dasharray: `${len.toFixed(4)} ${(circ - len).toFixed(4)}`,
                dashoffset: parseFloat((-cum).toFixed(4)),
                label: item.label,
                value: item.value,
            };
            cum += len;
            return seg;
        });

        return { segments, total, centerLabel: "Simulation" };
    }

    formatValue(value: number): string {
        return value.toLocaleString("en-US");
    }

    get simulationBarEntries(): Array<{
        label: string;
        total: number;
        topY: string;
        segments: Array<{ color: string; y: string; height: string; value: number; label: string }>;
    }> {
        const months = this.simulationMonthlyData?.monthlyData ?? [];
        if (!months.length) return [];
        const maxTotal = Math.max(...months.map((m) => m.totalSimulation), 1);
        const CHART_TOP = 15; // top padding so value labels are never clipped
        const H = 85;
        const baseline = CHART_TOP + H; // bars fill downward from this line
        return months.map((m) => {
            const seu = m.seuSimulation || 0;
            const sla = m.slaSimulation || 0;
            const err = Math.max(0, m.totalSimulation - seu - sla);
            const segments: Array<{ color: string; y: string; height: string; value: number; label: string }> = [];
            let y = baseline;
            const seuH = (seu / maxTotal) * H;
            if (seuH > 0) { y -= seuH; segments.push({ color: "#5b9bd5", y: y.toFixed(2), height: seuH.toFixed(2), value: seu, label: "SEU Simulation" }); }
            const slaH = (sla / maxTotal) * H;
            if (slaH > 0) { y -= slaH; segments.push({ color: "#6bba8a", y: y.toFixed(2), height: slaH.toFixed(2), value: sla, label: "SLA Simulation" }); }
            const errH = (err / maxTotal) * H;
            if (errH > 0) { y -= errH; segments.push({ color: "#e57373", y: y.toFixed(2), height: errH.toFixed(2), value: err, label: "Errors" }); }
            return { label: m.monthName, total: m.totalSimulation, topY: y.toFixed(2), segments };
        });
    }

    get simulationBarViewBox(): string {
        const n = this.simulationMonthlyData?.monthlyData?.length ?? 0;
        return `0 0 ${n * 50 + 20} 140`;
    }

    /** Formats a Date as YYYY-MM-DD using local time to avoid UTC timezone shift. */
    private toLocalDateString(date: Date): string {
        const y = date.getFullYear();
        const m = String(date.getMonth() + 1).padStart(2, "0");
        const d = String(date.getDate()).padStart(2, "0");
        return `${y}-${m}-${d}`;
    }

    onBookClosureChange(): void {
        const productionDate = this.selectedBookClosureDetail
            ? this.toLocalDateString(
                  new Date(this.selectedBookClosureDetail.productionDate),
              )
            : undefined;
        this.loadingMain = true;
        forkJoin({
            components: this.stateService.GetByAllocationType(
                AllocationTypes.Component,
                productionDate,
            ),
            finalAssembly: this.stateService.GetByAllocationType(
                AllocationTypes.FinalAssembly,
                productionDate,
            ),
        }).subscribe(({ components, finalAssembly }) => {
            this.updatePanelStats(0, components);
            this.updatePanelStats(1, finalAssembly);

            this.allocationOverviews = [
                this.buildOverview(this.panels[0], components),
                this.buildOverview(this.panels[1], finalAssembly),
                this.allocationOverviews[2],
            ];

            this.allocationByUnits = [
                {
                    title: "Component Allocation",
                    rows: this.mapOverviewToRows(components),
                },
                {
                    title: "Final Assembly Allocation",
                    rows: this.mapOverviewToRows(finalAssembly),
                },
            ];

            this.loadingMain = false;
        });
    }

    compareBookClosure(a: BookClosureDetails, b: BookClosureDetails): boolean {
        return a?.id === b?.id;
    }

    private loadbookClosureDetails(): void {
        this.stateService
            .GetBookClosure()
            .subscribe((data: BookClosureDetails | BookClosureDetails[]) => {
                const items: BookClosureDetails[] = Array.isArray(data)
                    ? data
                    : data
                      ? [data]
                      : [];
                if (items.length) {
                    this.bookClosureDetails = items;
                    const now = new Date();
                    // strip time so toDate "2026-08-13T00:00:00" still matches today
                    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
                    const match = items.find((item) => {
                        const from = new Date(item.fromDate);
                        const to = new Date(item.toDate);
                        // normalise to midnight so comparison is date-only
                        from.setHours(0, 0, 0, 0);
                        to.setHours(0, 0, 0, 0);
                        return today >= from && today <= to;
                    });
                    this.selectedBookClosureDetail = match ?? items[0];

                    console.log(
                        "Loaded book closure details:",
                        this.selectedBookClosureDetail,
                    );
                }
                // load dashboard data only after selectedBookClosureDetail is resolved
                this.loadDashboardData();
            });
    }
}
